import {
    HsabAccountNametag,
    HsabExpenseCategory,
    HsabExpenseCategoryDisplay,
    HsabExpenseCategoryResponse,
    HsabExpenseTypeResponse,
    HsabPlanSubType,
    HsabPlanType,
    WexExpenseAccountType
} from "@/shared/types";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import { useEmployerBankSettingsStore } from "@/shared/store/employerBankSettings";

interface EligiblePlanCombo {
    type: HsabPlanType;
    anySubType: boolean;
    subTypes?: HsabPlanSubType[];
}

export interface PlanTypesAndSubTypes {
    types: HsabPlanType[];
    subTypes: HsabPlanSubType[];
    allowHealthcareClaims: boolean;
    hasSmartCommutePlan: boolean;
    hasMedicalPlan: boolean;
}

export interface Categories {
    [category: string]: HsabExpenseCategoryResponse;
}

export function getAllDisplayCategories (categories: Categories) {
    const featureFlagStore = useFeatureFlagStore();
    const employerBankSettingsStore = useEmployerBankSettingsStore();
    const results: HsabExpenseCategoryDisplay[] = [];

    if (includeCategoryInResults(categories, HsabExpenseCategory.Medical)) {
        results.push(MedicalCategoryDisplay);
    }
    if (includeCategoryInResults(categories, HsabExpenseCategory.Dental)) {
        results.push(DentalCategoryDisplay);
    }
    if (includeCategoryInResults(categories, HsabExpenseCategory.Vision)) {
        results.push(VisionCategoryDisplay);
    }
    if (includeCategoryInResults(categories, HsabExpenseCategory.Pharmacy)) {
        results.push(PharmacyCategoryDisplay);
    }
    if (includeCategoryInResults(categories, HsabExpenseCategory.Premium)) {
        results.push(PremiumCategoryDisplay);
    }
    if (includeCategoryInResults(categories, HsabExpenseCategory.DependentCare)) {
        results.push(DependentCareCategoryDisplay);
    }
    if (includeCategoryInResults(categories, HsabExpenseCategory.Parking)) {
        results.push(ParkingCategoryDisplay);
    }

    const forceShowMassTransit = featureFlagStore.flags.bend22263MassTransitExpenseCategory;
    const employerSettingIsOn = featureFlagStore.flags.bend22264EnableMassTransitPlanSettings
        && employerBankSettingsStore.isEmployerMassTransitPlanStatusActive;
    if ((forceShowMassTransit || employerSettingIsOn)
        && includeCategoryInResults(categories, HsabExpenseCategory.MassTransit)) {
        results.push(MassTransitCategoryDisplay);
    }
    if (includeCategoryInResults(categories, HsabExpenseCategory.Lifestyle)) {
        results.push(LifestyleCategoryDisplay);
    }
    if (includeCategoryInResults(categories, HsabExpenseCategory.Tuition)) {
        results.push(TuitionCategoryDisplay);
    }

    return results;
}

export function includeCategoryInResults (activeCategoriesForUser: Categories, category: HsabExpenseCategory): boolean {
    return activeCategoriesForUser[category] !== undefined;
}

export const MedicalCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Medical,
    external: WexExpenseAccountType.Medical,
    display: "Medical",
    showTypeSelector: true,
    defaultTypeWhenSelected: "Medical Expenses",
    defaultSubTypeWhenSelected: plans => {
        if (hasLimitedFsaPlan(plans)) {
            return "Medical Services";
        }
        return null;
    },
};

export const DentalCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Dental,
    external: WexExpenseAccountType.Medical,
    display: "Dental",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Dental",
};

export const VisionCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Vision,
    external: WexExpenseAccountType.Medical,
    display: "Vision",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Vision",
};

export const PharmacyCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Pharmacy,
    external: WexExpenseAccountType.Medical,
    display: "Pharmacy",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Drugs & Medicine",
    defaultSubTypeWhenSelected: plans => {
        if (hasLimitedFsaPlan(plans)) {
            return "Other Drugs & Medicine";
        }
        return null;
    },
};

export const DependentCareCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.DependentCare,
    external: WexExpenseAccountType.DependentCare,
    display: "Dependent Care",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
};

export const ParkingCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Parking,
    external: WexExpenseAccountType.Transportation,
    display: "Parking",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Transportation - Parking",
};

export const MassTransitCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.MassTransit,
    external: WexExpenseAccountType.Transportation,
    display: "Mass Transit",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Transportation",
};

export const PremiumCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Premium,
    external: WexExpenseAccountType.Premium,
    display: "Premium",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
};

export const LifestyleCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Lifestyle,
    external: WexExpenseAccountType.Lifestyle,
    display: "Lifestyle",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
};

export const TuitionCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Tuition,
    external: WexExpenseAccountType.Tuition,
    display: "Tuition",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
};

export function hasLimitedFsaPlan (plans: HsabAccountNametag[]) {
    return plans.some(x => x.isActive && x.planType === HsabPlanType.MedicalFlex && x.planSubType === HsabPlanSubType.LimitedPurposeFsa);
}

export function removeItemsIfUserHasLimitedFsaPlan (plans: HsabAccountNametag[], items: HsabExpenseTypeResponse[]): HsabExpenseTypeResponse[] {
    if (hasLimitedFsaPlan(plans)) {
        for (const type of items) {
            type.expenseSubTypes = type.expenseSubTypes.filter(x => x.planEligibility.some(y => y.planDisplayName !== "HSA"));
        }

        return items.filter(x => x.expenseSubTypes.some(y => y.planEligibility.some(z => z.planDisplayName !== "HSA")));
    }
    return items;
}